import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import {
  AboutDetailsImageUpload,
  getAboutDetails,
  updateAbout,
} from "./services/about.services";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormTextField from "../../components/formFields/FormTextField";

const About: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const [formKey, setFormKey] = React.useState(1);

  const loading = useAppSelector((state) => state.about.loading);
  const AboutDetails = useAppSelector((state) => state.about.AboutDetails);

  useEffect(() => {
    dispatch(getAboutDetails());
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AboutDetails]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const AboutImage =
      values?.AboutImage && typeof values?.AboutImage !== "string"
        ? values.AboutImage[0].getRawFile()
        : "";
    const VisionIcon =
      values?.VisionIcon && typeof values?.VisionIcon !== "string"
        ? values.VisionIcon[0].getRawFile()
        : "";
    const VisionImage =
      values?.VisionImage && typeof values?.VisionImage !== "string"
        ? values.VisionImage[0].getRawFile()
        : "";
    const GoalIcon =
      values?.GoalIcon && typeof values?.GoalIcon !== "string"
        ? values.GoalIcon[0].getRawFile()
        : "";
    const GoalImage =
      values?.GoalImage && typeof values?.GoalImage !== "string"
        ? values.GoalImage[0].getRawFile()
        : "";
    const MissionIcon =
      values?.MissionIcon && typeof values?.MissionIcon !== "string"
        ? values.MissionIcon[0].getRawFile()
        : "";
    const MissionImage =
      values?.MissionImage && typeof values?.MissionImage !== "string"
        ? values.MissionImage[0].getRawFile()
        : "";

    try {
      const payload = {
        AboutDescription: values?.AboutDescription
          ? values?.AboutDescription
          : "",
        VisionDescription: values?.VisionDescription
          ? values?.VisionDescription
          : "",
        GoalDescription: values?.GoalDescription ? values?.GoalDescription : "",
        MissionDescription: values?.MissionDescription
          ? values?.MissionDescription
          : "",
      };
      const response = await dispatch(updateAbout(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        if (
          (values?.AboutImage && typeof values?.AboutImage !== "string") ||
          (values?.VisionIcon && typeof values?.VisionIcon !== "string") ||
          (values?.VisionImage && typeof values?.VisionImage !== "string") ||
          (values?.GoalIcon && typeof values?.GoalIcon !== "string") ||
          (values?.GoalImage && typeof values?.GoalImage !== "string") ||
          (values?.MissionIcon && typeof values?.MissionIcon !== "string") ||
          (values?.MissionImage && typeof values?.MissionImage !== "string") ||
          values?.AboutAltText ||
          values?.VisionAltText ||
          values?.GoalAltText ||
          values?.MissionAltText
        ) {
          ImageData.append("AboutImage", AboutImage);
          ImageData.append("AboutAltText", values?.AboutAltText);
          ImageData.append("VisionIcon", VisionIcon);
          ImageData.append("VisionImage", VisionImage);
          ImageData.append("VisionAltText", values?.VisionAltText);
          ImageData.append("GoalIcon", GoalIcon);
          ImageData.append("GoalImage", GoalImage);
          ImageData.append("GoalAltText", values?.GoalAltText);
          ImageData.append("MissionIcon", MissionIcon);
          ImageData.append("MissionImage", MissionImage);
          ImageData.append("MissionAltText", values?.MissionAltText);
          ImageData.append("ID", values?.ID);
          await dispatch(AboutDetailsImageUpload(ImageData));
        }
        dispatch(getAboutDetails());
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={AboutDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <Typography.h4>{"About Setting"}</Typography.h4>
            <ShadowCard style={{ padding: 12, margin: "20px 0" }}>
              <GridLayout
                gap={{ rows: 10, cols: 20 }}
                cols={[{ width: "70%" }, { width: "30%" }]}
                style={{ marginRight: 20 }}
              >
                <GridLayoutItem colSpan={2}>
                  <Typography.h5
                    style={{
                      marginBottom: "6px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    About Amazon Uniform
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="AboutDescription"
                    label="About Description"
                    component={FormRichTextField}
                    minHeight={300}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    justifyContent: "center",
                  }}
                >
                  <Field
                    justifyContent="start"
                    name={`AboutImage`}
                    label="About Image"
                    type="file"
                    accept="image/*"
                    alternateName="About Image"
                    component={FormImageUpload}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name={`AboutAltText`}
                    label="About Alt. Text"
                    placeholder="About Alt. Text"
                    component={FormTextField}
                  />
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ padding: 12, margin: "20px 0" }}>
              <GridLayout
                gap={{ rows: 10, cols: 20 }}
                cols={[{ width: "70%" }, { width: "30%" }]}
                style={{ marginRight: 20 }}
              >
                <GridLayoutItem colSpan={2}>
                  <Typography.h5
                    style={{
                      marginBottom: "6px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    Out Vision, Goal & Mission
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="VisionDescription"
                    label="Vision Description"
                    component={FormRichTextField}
                    minHeight={300}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    justifyContent: "center",
                  }}
                >
                  <Field
                    justifyContent="start"
                    name={`VisionIcon`}
                    label="Vision Icon Image"
                    type="file"
                    accept="image/*"
                    alternateName="Vision Icon Image"
                    component={FormImageUpload}
                  />
                  <Field
                    justifyContent="start"
                    name={`VisionImage`}
                    label="Vision Image"
                    type="file"
                    accept="image/*"
                    alternateName="Vision Image"
                    component={FormImageUpload}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name={`VisionAltText`}
                    label="Vision Alt. Text"
                    placeholder="Vision Alt. Text"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <hr />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="GoalDescription"
                    label="Goal Description"
                    component={FormRichTextField}
                    minHeight={300}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    justifyContent: "center",
                  }}
                >
                  <Field
                    justifyContent="start"
                    name={`GoalIcon`}
                    label="Goal Icon Image"
                    type="file"
                    accept="image/*"
                    alternateName="Goal Icon Image"
                    component={FormImageUpload}
                  />
                  <Field
                    justifyContent="start"
                    name={`GoalImage`}
                    label="Goal Image"
                    type="file"
                    accept="image/*"
                    alternateName="Goal Image"
                    component={FormImageUpload}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name={`GoalAltText`}
                    label="Goal Alt. Text"
                    placeholder="Goal Alt. Text"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <hr />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="MissionDescription"
                    label="Mission Description"
                    component={FormRichTextField}
                    minHeight={300}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    justifyContent: "center",
                  }}
                >
                  <Field
                    justifyContent="start"
                    name={`MissionIcon`}
                    label="Mission Icon Image"
                    type="file"
                    accept="image/*"
                    alternateName="Mission Icon Image"
                    component={FormImageUpload}
                  />
                  <Field
                    justifyContent="start"
                    name={`MissionImage`}
                    label="Mission Image"
                    type="file"
                    accept="image/*"
                    alternateName="Mission Image"
                    component={FormImageUpload}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name={`MissionAltText`}
                    label="Mission Alt. Text"
                    placeholder="Mission Alt. Text"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={2}
                  style={{
                    textAlign: "end",
                    marginTop: 20,
                    width: "100%",
                  }}
                >
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginRight: 10 }}
                  >
                    Cancel
                  </Button>
                  <ButtonWithLoading
                    label={"Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default About;
